/**
 * CopilotLauncher — top-bar Sparkles button. Mounted next to the Alarms bell in `_layout.tsx`.
 *
 * Where the platform answers the AI Casting Consultant (lib/copilot/availability), it opens the consultant's
 * conversation (2026-09-26), with the context of the page the person is on (2026-09-28: lib/copilot/ask), so the chip
 * above the composer says where they were; nothing is drafted. The conversation is in the right-hand panel, which this
 * button opens and closes (2026-10-06, launcherAction); on the consultant's own page, and for a role that may not use
 * it, the button opens the page, as before. Where no consultant runs, it toggles the global CopilotPanel, as before:
 * `Cmd/Ctrl+J` is the keyboard equivalent, registered inside the CopilotPanel itself, and the badge counts the
 * *undecided* proposals in that conversation.
 */

import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "@/lib/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/ui/tooltip";
import { useOptionalCopilot } from "@/lib/copilot/store";
import { COPILOT_COMING_SOON, COPILOT_COMING_SOON_LABEL } from "@/lib/copilot/flags";
import { consultantLauncher, panelContent } from "@/lib/copilot/availability";
import { launcherAction } from "@/lib/copilot/ask";
import { LAUNCHER_ATTR } from "@/lib/copilot/selection";
import { useCopilotAvailability } from "@/lib/api/copilot-imports";

export function CopilotLauncher() {
  const copilot = useOptionalCopilot();
  const availability = useCopilotAvailability();
  const launcher = consultantLauncher(availability);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const undecidedCount = useMemo(() => {
    if (!copilot) return 0;
    let count = 0;
    for (const m of copilot.messages) {
      if (!m.proposals) continue;
      for (const p of m.proposals) {
        if (!copilot.decided[p.id] && p.apply) count++;
      }
    }
    return count;
  }, [copilot]);

  if (!copilot) return null;

  if (launcher.opensPage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={launcher.label}
            // It opens and closes the panel where the consultant answers in it (#1186 review LOW 3).
            aria-expanded={copilot.isOpen}
            {...{ [LAUNCHER_ATTR]: "" }}
            onClick={() => {
              const action = launcherAction(panelContent(availability, pathname), copilot.isOpen, copilot.pageContext, pathname);
              if (action.kind === "ask") copilot.ask(action.request);
              else if (action.kind === "close") copilot.close();
              else {
                copilot.close(); // the Phase-0 panel, if it was left open, gives way to the page
                navigate(action.href);
              }
            }}
          >
            <Sparkles className="size-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{launcher.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={COPILOT_COMING_SOON ? launcher.label : "AI Casting Consultant"}
          aria-expanded={copilot.isOpen}
          {...{ [LAUNCHER_ATTR]: "" }}
          onClick={copilot.toggle}
          className="relative"
        >
          <Sparkles className="size-5" />
          {/* Demo gating (#117): suppress the pending-proposals count and
              show a small "coming soon" dot instead. */}
          {COPILOT_COMING_SOON ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-warning ring-2 ring-surface-elevated"
            />
          ) : (
            undecidedCount > 0 && (
              <span
                aria-label={`${undecidedCount} pending proposal${undecidedCount === 1 ? "" : "s"}`}
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 font-mono text-[9px] font-semibold text-brand-foreground"
              >
                {undecidedCount}
              </span>
            )
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {COPILOT_COMING_SOON
          ? `AI Casting Consultant · ${COPILOT_COMING_SOON_LABEL}`
          : `AI Casting Consultant ${undecidedCount > 0 ? `· ${undecidedCount} pending` : ""} · ⌘/Ctrl+J`}
      </TooltipContent>
    </Tooltip>
  );
}
